import React from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LangContext";

// Section title per language
const i18n = {
  ru: { kicker: 'СМИ о нас', title: 'Нас упоминают в медиа', read: 'Читать статью' },
  uk: { kicker: 'ЗМІ про нас', title: 'Нас згадують у медіа', read: 'Читати статтю' },
  en: { kicker: 'In the press', title: 'Featured in the media', read: 'Read article' },
  ar: { kicker: 'في الإعلام', title: 'كُتب عنا في وسائل الإعلام', read: 'اقرأ المقال' },
  it: { kicker: 'Sulla stampa', title: 'Parlano di noi i media', read: 'Leggi articolo' },
  zh: { kicker: '媒体报道', title: '媒体对我们的报道', read: '阅读文章' },
  es: { kicker: 'En los medios', title: 'Hablan de nosotros en los medios', read: 'Leer artículo' },
  pl: { kicker: 'W mediach', title: 'Mówią o nas media', read: 'Czytaj artykuł' }
};

const mentions = [
  {
    id: 'forbes-kz',
    outlet: 'Forbes',
    outletSub: 'Kazakhstan',
    accent: '#D4A045',
    title: {
      ru: 'Как поступить в топовый мировой вуз за 24 часа',
      uk: 'Як вступити до топового світового вишу за 24 години',
      en: 'How to enrol in a top world university in 24 hours',
      ar: 'كيف تلتحق بجامعة عالمية رائدة في 24 ساعة',
      it: 'Come essere ammessi in un top university mondiale in 24 ore',
      zh: '如何在 24 小时内进入世界顶级大学',
      es: 'Cómo ingresar a una universidad top del mundo en 24 horas',
      pl: 'Jak dostać się na czołową światową uczelnię w 24 godziny'
    },
    url: 'https://forbes.kz/process/education/kak_postupit_v_topovyiy_mirovoy_vuz_za_24_chasa'
  },
  {
    id: 'forbes-ua',
    outlet: 'Forbes',
    outletSub: 'Ukraine',
    accent: '#D4A045',
    title: {
      ru: '«Мечта — это чёткий план, поэтому она всегда реальна»: как STUDYGRI помогает получить образование за рубежом',
      uk: '«Мрія — це чіткий план, тому вона завжди реальна»: як STUDYGRI допомагає здобути освіту за кордоном',
      en: '"A dream is a clear plan, that\u2019s why it\u2019s always real": how STUDYGRI helps to study abroad',
      ar: '«الحلم خطة واضحة، لذلك دائماً ما يكون حقيقياً»: كيف يساعد STUDYGRI في الدراسة بالخارج',
      it: '«Un sogno è un piano chiaro, per questo è sempre reale»: come STUDYGRI aiuta a studiare all\u2019estero',
      zh: '"梦想是清晰的计划，所以总是真实的"：STUDYGRI 如何帮助你出国留学',
      es: '«Un sueño es un plan claro, por eso siempre es real»: cómo STUDYGRI ayuda a estudiar en el extranjero',
      pl: '«Marzenie to jasny plan, dlatego zawsze jest realne»: jak STUDYGRI pomaga zdobyć wykształcenie za granicą'
    },
    url: 'https://forbes.ua/partners/mriya-tse-chitkiy-plan-tomu-vona-zavzhdi-realna-yak-studygri-dopomagae-zdobuti-osvitu-za-kordonom'
  },
  {
    id: 'the-village',
    outlet: 'The Village',
    outletSub: 'Україна',
    accent: '#1B2A4A',
    title: {
      ru: 'Образование за рубежом: что нужно знать перед поступлением',
      uk: 'Освіта за кордоном: що варто знати перед вступом',
      en: 'Studying abroad: what you need to know before applying',
      ar: 'الدراسة بالخارج: ما يجب معرفته قبل التقديم',
      it: 'Studiare all\u2019estero: cosa sapere prima di candidarsi',
      zh: '出国留学：申请前你需要知道的事',
      es: 'Estudiar en el extranjero: lo que necesitas saber antes de aplicar',
      pl: 'Studia za granicą: co warto wiedzieć przed aplikacją'
    },
    url: 'https://www.the-village.com.ua/village/business/work/318389'
  }
];

const OutletLogo = ({ outlet, sub, accent }) => (
  <div className="flex items-baseline gap-2" data-testid={`media-logo-${outlet.toLowerCase().replace(/\s+/g,'-')}`}>
    <span
      className="font-display tracking-tight"
      style={{
        fontFamily: "'Forum', 'Cormorant Garamond', serif",
        fontSize: '34px',
        lineHeight: 1,
        color: '#1B2A4A',
        fontWeight: 400,
        letterSpacing: '-0.02em'
      }}
    >
      {outlet}
    </span>
    <span
      className="text-[11px] font-bold tracking-[0.16em] uppercase px-1.5 py-0.5 rounded"
      style={{ background: accent, color: '#fff' }}
    >
      {sub}
    </span>
  </div>
);

const Media = () => {
  const { lang } = useLang();
  const tr = i18n[lang] || i18n.ru;
  const pick = (obj) => obj[lang] || obj.ru;

  return (
    <section className="py-20 md:py-28" style={{ background: '#FAF8F5' }} data-testid="media-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2
            className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05]"
            style={{ fontFamily: "'Forum', 'Cormorant Garamond', serif", color: '#1B2A4A' }}
          >
            {tr.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {mentions.map((m) => (
            <a
              key={m.id}
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`media-card-${m.id}`}
              className="group relative flex flex-col rounded-2xl bg-white p-7 md:p-8 transition-all duration-300 hover:-translate-y-1"
              style={{
                border: '1px solid rgba(27,42,74,0.08)',
                boxShadow: '0 4px 24px -8px rgba(27,42,74,0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 24px 50px -12px rgba(27,42,74,0.20)';
                e.currentTarget.style.borderColor = 'rgba(201,169,110,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 24px -8px rgba(27,42,74,0.08)';
                e.currentTarget.style.borderColor = 'rgba(27,42,74,0.08)';
              }}
            >
              <div className="pb-6 mb-6 border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                <OutletLogo outlet={m.outlet} sub={m.outletSub} accent={m.accent} />
              </div>

              <p
                className="text-base md:text-[17px] leading-snug flex-grow"
                style={{ color: '#1B2A4A', fontWeight: 500 }}
              >
                {pick(m.title)}
              </p>

              <div
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                style={{ color: '#C9A96E' }}
              >
                <span className="border-b border-current pb-0.5">{tr.read}</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Media;
